import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
} from "@mui/material";
import type { Badge, BadgeFormData } from "./types/badges.types";
import CustomTextField from "../../../components/common/Textfield";

interface BadgeDialogProps {
  open: boolean;
  badge?: Badge | null;
  onClose: () => void;
  onSave: (badgeData: BadgeFormData) => void;
}

const BADGE_ICONS = [
  "🎯",
  "⭐",
  "🏆",
  "⚡",
  "💯",
  "🏅",
  "🏰",
  "🔥",
  "💎",
  "✨",
  "🎓",
  "📜",
];

export default function BadgeDialog({
  open,
  badge,
  onClose,
  onSave,
}: BadgeDialogProps) {
  const [formData, setFormData] = useState<BadgeFormData>({
    name: "",
    description: "",
    icon: "🏅",
    condition: "",
    issueRule: "auto",
  });

  useEffect(() => {
    if (badge) {
      setFormData({
        name: badge.name,
        description: badge.description,
        icon: badge.icon,
        condition: badge.condition,
        issueRule: badge.issueRule,
      });
    } else {
      setFormData({
        name: "",
        description: "",
        icon: "🏅",
        condition: "",
        issueRule: "auto",
      });
    }
  }, [badge, open]);

  const handleSubmit = () => {
    onSave(formData);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxWidth: 720,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        {badge ? "Edit Badge" : "Create Badge"}
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Badge Icon Selector */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Badge Icon
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {BADGE_ICONS.map((icon) => (
                <IconButton
                  key={icon}
                  onClick={() => setFormData({ ...formData, icon })}
                  sx={{
                    width: 56,
                    height: 56,
                    border: "2px solid",
                    borderColor: formData.icon === icon ? "#8b5cf6" : "#e5e7eb",
                    borderRadius: 2,
                    fontSize: "1.75rem",
                    "&:hover": {
                      borderColor: "#8b5cf6",
                      backgroundColor: "#faf5ff",
                    },
                  }}
                >
                  {icon}
                </IconButton>
              ))}
            </Box>
          </Box>

          {/* Badge Name */}
          <CustomTextField
            label="Badge Name"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Module Master"
          />

          {/* Description */}
          <CustomTextField
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            placeholder="Describe how students can earn this badge..."
          />

          {/* Issue Rule */}
          <FormControl fullWidth>
            <InputLabel>Issue Rule</InputLabel>
            <Select
              value={formData.issueRule}
              label="Issue Rule"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  issueRule: e.target.value as "auto" | "manual",
                })
              }
            >
              <MenuItem value="auto">Auto-issue when condition is met</MenuItem>
              <MenuItem value="manual">Manual issue by instructor</MenuItem>
            </Select>
          </FormControl>

          {/* Condition */}
          <Box>
            <CustomTextField
              label="Condition"
              fullWidth
              value={formData.condition}
              onChange={(e) =>
                setFormData({ ...formData, condition: e.target.value })
              }
              placeholder="e.g., Complete all modules, Score >= 80%"
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              Describe the condition that triggers this badge
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: "none",
            color: "text.primary",
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            px: 3,
          }}
        >
          {badge ? "Save Changes" : "Create Badge"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
